import { useRef, useState } from "react";
import { useEffectOnce } from "react-use";
import * as fabric from "fabric";
import JsBarcode from "jsbarcode";

export default function App() {
  const canvasRef = useRef<fabric.Canvas | null>(null);
  const canvasElRef = useRef<HTMLCanvasElement>(null);
  const [selectedObject, setSelectedObject] = useState<fabric.Object | null>(null);
  const [barcodeValue, setBarcodeValue] = useState("B1-SHBLXL");
  const [barcodeType, setBarcodeType] = useState("CODE128");
  const [labelWidthMm, setLabelWidthMm] = useState(35);
  const [labelHeightMm, setLabelHeightMm] = useState(25);

  const mmToPx = (mm: number) => Math.round(mm * 8);

  const updateCanvasSize = () => {
    const widthPx = mmToPx(labelWidthMm);
    const heightPx = mmToPx(labelHeightMm);
    if (canvasRef.current) {
      canvasRef.current.setWidth(widthPx);
      canvasRef.current.setHeight(heightPx);
      canvasRef.current.backgroundColor = "#fff";
      canvasRef.current.renderAll();
    }
  };

  useEffectOnce(() => {
    if (canvasRef.current) {
      canvasRef.current.dispose();
    }

    const canvas = new fabric.Canvas(canvasElRef.current!, {
      width: mmToPx(labelWidthMm),
      height: mmToPx(labelHeightMm),
      backgroundColor: "#fff",
    });

    canvasRef.current = canvas;

    canvas.on("selection:created", handleSelection);
    canvas.on("selection:updated", handleSelection);
    canvas.on("selection:cleared", () => setSelectedObject(null));
  });

  const handleSelection = () => {
    const active = canvasRef.current?.getActiveObject();
    setSelectedObject(active || null);
  };

  const addText = () => {
    const text = new fabric.IText("Edit me", {
      left: 50,
      top: 50,
      fontSize: 20,
      fill: "#000",
    });
    canvasRef.current?.add(text);
    canvasRef.current?.setActiveObject(text);
  };

  const addImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      fabric.Image.fromURL(reader.result as string, {
        crossOrigin: "anonymous",
      }).then((img) => {
        img.set({ left: 100, top: 100, scaleX: 0.5, scaleY: 0.5 });
        canvasRef.current?.add(img);
        canvasRef.current?.setActiveObject(img);
      });
    };
    reader.readAsDataURL(file);
  };

  const addBarcode = () => {
    const tempCanvas = document.createElement("canvas");
    try {
      JsBarcode(tempCanvas, barcodeValue, {
        format: barcodeType,
        displayValue: false,
        width: 1,
        height: 40,
      });
      const barcodeImgUrl = tempCanvas.toDataURL("image/png");

      fabric.Image.fromURL(barcodeImgUrl, {
        crossOrigin: "anonymous",
      }).then((barcodeImg) => {
        barcodeImg.set({ left: 50, top: 50, scaleX: 1, scaleY: 1 });

        const label = new fabric.IText(barcodeValue, {
          fontSize: 12,
          fill: "#000",
          fontFamily: "Arial",
          top: barcodeImg.top! + 45,
          left: barcodeImg.left,
          originX: 'left',
          selectable: true,
          evented: true,
          hasControls: true,
        });

        canvasRef.current?.add(barcodeImg);
        canvasRef.current?.add(label);
        canvasRef.current?.setActiveObject(label);
      });
    } catch (err) {
      alert("Invalid barcode or format");
    }
  };

  const deleteSelected = () => {
    const active = canvasRef.current?.getActiveObject();
    if (active) {
      canvasRef.current?.remove(active);
      setSelectedObject(null);
    }
  };

  const printLabel = () => {
    if (!canvasRef.current) return;
    const dataUrl = canvasRef.current.toDataURL({ format: "png", multiplier: 1 });
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html>
        <head>
          <style>
            @page {
              size: ${labelWidthMm}mm ${labelHeightMm}mm;
              margin: 0;
            }
            body {
              margin: 0;
              display: flex;
              justify-content: center;
              align-items: center;
              height: 100vh;
            }
            img {
              width: 100%;
              height: auto;
            }
          </style>
        </head>
        <body>
          <img src="${dataUrl}" onload="window.print();window.close();" />
        </body>
      </html>
    `);
    win.document.close();
  };

  const updateTextProperty = (key: keyof fabric.IText, value: any) => {
    if (selectedObject && selectedObject.type === "i-text") {
      (selectedObject as fabric.IText).set(key, value);
      canvasRef.current?.renderAll();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, padding: 20 }}>
      <div style={{ display: "flex", gap: 10 }}>
        <label>
          Width (mm):
          <input type="number" value={labelWidthMm} onChange={(e) => setLabelWidthMm(Number(e.target.value))} />
        </label>
        <label>
          Height (mm):
          <input type="number" value={labelHeightMm} onChange={(e) => setLabelHeightMm(Number(e.target.value))} />
        </label>
        <button onClick={updateCanvasSize}>Apply Size</button>
      </div>

      <div style={{ display: selectedObject ? "flex" : "none", gap: 10, marginBottom: 10 }}>
        {selectedObject?.type === "i-text" && (
          <>
            <select
              onChange={(e) => updateTextProperty("fontFamily", e.target.value)}
              value={(selectedObject as fabric.IText)?.fontFamily || "Arial"}
            >
              <option value="Arial">Arial</option>
              <option value="Courier New">Courier New</option>
              <option value="Georgia">Georgia</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Verdana">Verdana</option>
            </select>
            <input
              type="number"
              value={(selectedObject as fabric.IText)?.fontSize || 20}
              onChange={(e) => updateTextProperty("fontSize", parseInt(e.target.value))}
              style={{ width: 60 }}
            />
            <input
              type="color"
              value={(selectedObject as fabric.IText)?.fill as string || "#000000"}
              onChange={(e) => updateTextProperty("fill", e.target.value)}
            />
            <button onClick={() => updateTextProperty("fontWeight", (selectedObject as fabric.IText)?.fontWeight === "bold" ? "normal" : "bold")}>
              Bold
            </button>
            <button onClick={() => updateTextProperty("fontStyle", (selectedObject as fabric.IText)?.fontStyle === "italic" ? "normal" : "italic")}>
              Italic
            </button>
            <button onClick={() => updateTextProperty("underline", !(selectedObject as fabric.IText)?.underline)}>
              Underline
            </button>
          </>
        )}
        <button onClick={deleteSelected}>Delete</button>
      </div>

      <canvas ref={canvasElRef} style={{ border: "1px solid #ccc", borderRadius: 6 }} />

      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={addText}>Add Text</button>
        <button onClick={printLabel}>Print</button>
        <label style={{ display: "inline-block", padding: "6px 12px", background: "#eee", cursor: "pointer" }}>
          Upload Image
          <input type="file" onChange={addImage} accept="image/*" style={{ display: "none" }} />
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <select value={barcodeType} onChange={(e) => setBarcodeType(e.target.value)}>
          <option value="CODE128">CODE128</option>
          <option value="CODE128A">CODE128A</option>
          <option value="CODE128B">CODE128B</option>
          <option value="EAN13">EAN13</option>
          <option value="UPC">UPC</option>
        </select>
        <input
          type="text"
          value={barcodeValue}
          onChange={(e) => setBarcodeValue(e.target.value)}
          placeholder="Barcode data"
        />
        <button onClick={addBarcode}>Add Barcode</button>
      </div>
    </div>
  );
}
